import { formatCurrency } from '@/utils/formatCurrency';
import type { BudgetUsageEvent } from '@/types/budgetUsage';
import type { Portfolio } from '@/types/portfolios';
import type { SpCampaignNormalized } from '@/types/spCampaigns';

export const BUDGET_USAGE_AT_RISK_THRESHOLD = 80;
export const BUDGET_USAGE_OUT_OF_BUDGET_THRESHOLD = 95;

export function dashboardDateRangeToStreamTime(
  startDate: string,
  endDate: string
): { startTime: string; endTime: string } {
  return {
    startTime: `${startDate}T00:00:00.000Z`,
    endTime: `${endDate}T23:59:59.999Z`,
  };
}

export function formatBudgetUsageDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

export function formatBudgetCurrency(
  amount: number | null | undefined,
  currencyCode?: string | null
): string {
  return formatCurrency(
    amount,
    currencyCode ? { currencyCode } : undefined,
    { empty: '—' }
  );
}

export function formatUsagePercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(1)}%`;
}

export function formatOptionalText(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  return value;
}

export function hasExtraFields(extraFields: Record<string, unknown> | undefined): boolean {
  return Boolean(extraFields && Object.keys(extraFields).length > 0);
}

export function formatExtraFieldsJson(extraFields: Record<string, unknown> | undefined): string {
  if (!hasExtraFields(extraFields)) return '—';
  return JSON.stringify(extraFields, null, 2);
}

export function budgetUsageRowKey(event: BudgetUsageEvent, index: number): string {
  return event.id ?? `${event.idempotencyId}-${event.usageUpdatedAt}-${index}`;
}

export type BudgetUsageSortKey = 'budget' | 'usage';
export type BudgetUsageSortDirection = 'asc' | 'desc';

export function compareBudgetUsageNumbers(
  a: number | null | undefined,
  b: number | null | undefined,
  direction: BudgetUsageSortDirection
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return direction === 'asc' ? a - b : b - a;
}

export function sortBudgetUsageEvents(
  events: BudgetUsageEvent[],
  sortKey: BudgetUsageSortKey,
  direction: BudgetUsageSortDirection
): BudgetUsageEvent[] {
  const sorted = [...events];
  sorted.sort((left, right) => {
    const primary =
      sortKey === 'budget'
        ? compareBudgetUsageNumbers(left.budget, right.budget, direction)
        : compareBudgetUsageNumbers(left.budgetUsagePercent, right.budgetUsagePercent, direction);
    if (primary !== 0) return primary;
    return new Date(right.usageUpdatedAt).getTime() - new Date(left.usageUpdatedAt).getTime();
  });
  return sorted;
}

export type UsageRiskLevel = 'low' | 'medium' | 'high';

export function usageRiskLevel(percent: number | null | undefined): UsageRiskLevel {
  if (percent == null || !Number.isFinite(percent)) return 'low';
  if (percent >= BUDGET_USAGE_AT_RISK_THRESHOLD) return 'high';
  if (percent >= 50) return 'medium';
  return 'low';
}

export function buildCampaignNameMap(campaigns: SpCampaignNormalized[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const campaign of campaigns) {
    if (campaign.campaignId) map.set(campaign.campaignId, campaign.name);
  }
  return map;
}

export function buildPortfolioNameMap(portfolios: Portfolio[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const portfolio of portfolios) {
    if (portfolio.portfolioId) map.set(portfolio.portfolioId, portfolio.name);
  }
  return map;
}

export function resolveBudgetUsageEntityName(
  event: BudgetUsageEvent,
  campaignNames: Map<string, string>,
  portfolioNames: Map<string, string>
): { label: string; subId: string } {
  const scopeType = String(event.budgetScopeType ?? '').toUpperCase();
  const scopeId = event.budgetScopeId ?? '';
  if (scopeType === 'PORTFOLIO') {
    const id = event.portfolioId ?? scopeId;
    return {
      label: portfolioNames.get(id) ?? `Portfolio ${id}`,
      subId: id,
    };
  }
  const id = event.campaignId ?? scopeId;
  return {
    label: campaignNames.get(id) ?? `Campaign ${id}`,
    subId: id,
  };
}

export function budgetUsageScopeKey(event: BudgetUsageEvent): string {
  return `${String(event.budgetScopeType ?? '').toUpperCase()}:${event.budgetScopeId}`;
}

export function latestBudgetUsageEvent(events: BudgetUsageEvent[]): BudgetUsageEvent | null {
  if (events.length === 0) return null;
  return events.reduce((latest, event) => {
    const eventTime = new Date(event.usageUpdatedAt).getTime();
    const latestTime = new Date(latest.usageUpdatedAt).getTime();
    return eventTime > latestTime ? event : latest;
  }, events[0]);
}

export function latestBudgetUsagePerScope(events: BudgetUsageEvent[]): BudgetUsageEvent[] {
  const byScope = new Map<string, BudgetUsageEvent>();
  for (const event of events) {
    const key = budgetUsageScopeKey(event);
    const existing = byScope.get(key);
    if (!existing) {
      byScope.set(key, event);
      continue;
    }
    const existingTime = new Date(existing.usageUpdatedAt).getTime();
    const eventTime = new Date(event.usageUpdatedAt).getTime();
    if (eventTime > existingTime) byScope.set(key, event);
  }
  return Array.from(byScope.values()).sort(
    (a, b) => new Date(b.usageUpdatedAt).getTime() - new Date(a.usageUpdatedAt).getTime()
  );
}

export type BudgetUsageSummary = {
  entitiesAtRisk: number;
  outOfBudgetSignals: number;
  latestAlertAt: string | null;
  eventCount: number;
  hitLimit: boolean;
};

export function summarizeBudgetUsageEvents(
  events: BudgetUsageEvent[],
  rowCount: number,
  limit: number
): BudgetUsageSummary {
  const latestPerScope = latestBudgetUsagePerScope(events);
  const entitiesAtRisk = latestPerScope.filter(
    (e) => (e.budgetUsagePercent ?? 0) >= BUDGET_USAGE_AT_RISK_THRESHOLD
  ).length;
  const outOfBudgetSignals = events.filter(
    (e) => (e.budgetUsagePercent ?? 0) >= BUDGET_USAGE_OUT_OF_BUDGET_THRESHOLD
  ).length;
  const latestAlertAt =
    events.length > 0
      ? events.reduce((latest, e) => {
          const t = new Date(e.usageUpdatedAt).getTime();
          return t > new Date(latest).getTime() ? e.usageUpdatedAt : latest;
        }, events[0].usageUpdatedAt)
      : null;

  return {
    entitiesAtRisk,
    outOfBudgetSignals,
    latestAlertAt,
    eventCount: rowCount,
    hitLimit: rowCount >= limit,
  };
}

export function filterBudgetUsageEvents(
  events: BudgetUsageEvent[],
  options: {
    scopeType: 'ALL' | 'CAMPAIGN' | 'PORTFOLIO';
    minUsagePercent: number | null;
    search: string;
    campaignNames: Map<string, string>;
    portfolioNames: Map<string, string>;
    allowedCampaignIds?: string[] | null;
  }
): BudgetUsageEvent[] {
  const query = options.search.trim().toLowerCase();
  const allowed =
    options.allowedCampaignIds != null ? new Set(options.allowedCampaignIds) : null;
  return events.filter((event) => {
    const scopeType = String(event.budgetScopeType ?? '').toUpperCase();
    if (allowed) {
      if (scopeType === 'PORTFOLIO') return false;
      const campaignId = event.campaignId ?? event.budgetScopeId;
      if (!campaignId || !allowed.has(campaignId)) return false;
    }
    if (options.scopeType !== 'ALL' && scopeType !== options.scopeType) return false;
    if (
      options.minUsagePercent != null &&
      (event.budgetUsagePercent ?? 0) < options.minUsagePercent
    ) {
      return false;
    }
    if (!query) return true;
    const { label, subId } = resolveBudgetUsageEntityName(
      event,
      options.campaignNames,
      options.portfolioNames
    );
    const haystack = [label, subId, event.budgetScopeId, event.campaignId, event.portfolioId]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(query);
  });
}
